/**
 * Telemetry Service — lightweight analytics for ELVOAQ.
 *
 * Collects page views, feature usage, and session data.
 * Sends events in batches every 30 seconds to minimize network overhead.
 * Respects user opt-out preference stored on the server and in localStorage.
 */

import { api } from './api';
import { getCookiePreferences, hasCookieConsent } from '../components/CookieConsent';

// --------------- Types ---------------

interface TelemetryEvent {
  event: string;
  category: 'navigation' | 'feature' | 'engagement' | 'performance';
  metadata?: Record<string, any>;
  timestamp: string;
  locale?: string;
  screen?: string;
  referrer?: string;
}

// --------------- State ---------------

let sessionId: string = '';
let queue: TelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;
let optedOut = false;
let pageEntryTime = 0;

const FLUSH_INTERVAL = 30_000; // 30 seconds
const STORAGE_KEY = '__eq_sid';
const OPT_OUT_KEY = '__eq_prefs_dx';

// --------------- Helpers ---------------

function generateSessionId(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

function getOrCreateSessionId(): string {
  let sid = sessionStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = generateSessionId();
    sessionStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
}

function getScreenSize(): string {
  return `${window.screen.width}x${window.screen.height}`;
}

function getLocale(): string {
  return navigator.language || 'en';
}

// --------------- Core ---------------

function enqueue(event: TelemetryEvent): void {
  if (optedOut || !initialized) return;
  // Respect cookie consent — only collect analytics if user accepted
  if (hasCookieConsent() && !getCookiePreferences().analytics) return;
  queue.push(event);

  // If queue gets big, flush immediately
  if (queue.length >= 20) {
    flush();
  }
}

async function flush(): Promise<void> {
  if (queue.length === 0 || optedOut) return;

  const batch = queue.splice(0, 50);

  try {
    await api.post('/telemetry/events', {
      sessionId,
      events: batch,
    });
  } catch {
    // If failed, put events back (only if still small)
    if (queue.length < 100) {
      queue.unshift(...batch);
    }
  }
}

// --------------- Public API ---------------

/**
 * Initialize the telemetry service. Call once on app startup.
 */
export function initTelemetry(): void {
  if (initialized) return;

  // Check local opt-out flag
  const localPref = localStorage.getItem(OPT_OUT_KEY);
  if (localPref === '1') {
    optedOut = true;
  }

  sessionId = getOrCreateSessionId();
  initialized = true;

  // Respect cookie consent preferences
  window.addEventListener('cookie-consent-changed', () => {
    if (hasCookieConsent() && !getCookiePreferences().analytics) {
      queue = [];
    }
  });

  // Start periodic flush
  flushTimer = setInterval(flush, FLUSH_INTERVAL);

  // Flush on page unload
  window.addEventListener('beforeunload', () => {
    // Record session end
    if (pageEntryTime > 0) {
      const duration = Math.round((Date.now() - pageEntryTime) / 1000);
      enqueue({
        event: 'session_end',
        category: 'engagement',
        metadata: { duration, pages: window.history.length },
        timestamp: new Date().toISOString(),
      });
    }
    flush();
  });

  // Track session start
  enqueue({
    event: 'session_start',
    category: 'engagement',
    metadata: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform,
    },
    timestamp: new Date().toISOString(),
    locale: getLocale(),
    screen: getScreenSize(),
    referrer: document.referrer || undefined,
  });

  pageEntryTime = Date.now();
}

/**
 * Track a page view.
 */
export function trackPageView(path: string): void {
  // Record time on previous page
  if (pageEntryTime > 0) {
    const duration = Math.round((Date.now() - pageEntryTime) / 1000);
    if (duration > 1) {
      enqueue({
        event: 'page_dwell',
        category: 'engagement',
        metadata: { duration },
        timestamp: new Date().toISOString(),
      });
    }
  }
  pageEntryTime = Date.now();

  enqueue({
    event: `view:${path}`,
    category: 'navigation',
    metadata: { path },
    timestamp: new Date().toISOString(),
    locale: getLocale(),
    screen: getScreenSize(),
  });
}

/**
 * Track a feature interaction.
 */
export function trackFeature(feature: string, metadata?: Record<string, any>): void {
  enqueue({
    event: `feature:${feature}`,
    category: 'feature',
    metadata,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a user engagement event.
 */
export function trackEngagement(action: string, metadata?: Record<string, any>): void {
  enqueue({
    event: `engage:${action}`,
    category: 'engagement',
    metadata,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a performance metric.
 */
export function trackPerformance(metric: string, value: number, metadata?: Record<string, any>): void {
  enqueue({
    event: `perf:${metric}`,
    category: 'performance',
    metadata: { value, ...metadata },
    timestamp: new Date().toISOString(),
  });
}

/**
 * Set opt-out status. Also syncs to server if authenticated.
 */
export async function setTelemetryOptOut(optOut: boolean): Promise<void> {
  optedOut = optOut;
  localStorage.setItem(OPT_OUT_KEY, optOut ? '1' : '0');

  if (optOut) {
    // Clear pending events
    queue = [];
  }

  try {
    await api.put('/telemetry/opt-out', { optOut });
  } catch {
    // Silently fail — local flag is authoritative
  }
}

/**
 * Check if user has opted out.
 */
export function isTelemetryOptedOut(): boolean {
  return optedOut;
}

/**
 * Load opt-out status from server (call after login).
 */
export async function syncTelemetryOptOut(): Promise<void> {
  try {
    const { data } = await api.get('/telemetry/opt-out');
    optedOut = data.optedOut === true;
    localStorage.setItem(OPT_OUT_KEY, optedOut ? '1' : '0');
  } catch {
    // Use local flag
  }
}

/**
 * Shutdown telemetry — flush remaining events.
 */
export function shutdownTelemetry(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush();
  initialized = false;
}
